import { PenLine } from "lucide-react";

import { useState } from "react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { useSmileSimulationViewport } from "../hooks/useSmileSimulationViewport";
import { formatSmileSimulationDateTime } from "../lib/session";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { ComparisonSlider } from "./ComparisonSlider";
import { SmileSimulationPatientSummary } from "./SmileSimulationPatientSummary";

type SmileSimulationRecordDetailPageProps = {
  comparison: SmileSimulationController["comparison"];
  download: SmileSimulationController["download"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  onBack: SmileSimulationController["actions"]["goBackToPatientDetail"];
  onDownloadResultImage: SmileSimulationController["actions"]["downloadSelectedRecordImage"];
  record: NonNullable<SmileSimulationController["history"]["selectedRecord"]>;
};

const getSimulationProviderLabel = (
  record: SmileSimulationRecordDetailPageProps["record"],
  unknownLabel: string,
) => {
  if (record.provider === "openai") {
    return `GPT Image 2${record.openAiQuality ? ` ${record.openAiQuality}` : ""}`;
  }

  if (record.provider === "google") {
    return "Nano Banana";
  }

  return record.imageModel ?? unknownLabel;
};

const getSimulationPromptLabel = (
  promptVersion: SmileSimulationRecordDetailPageProps["record"]["promptVersion"],
  unknownLabel: string,
) => {
  if (promptVersion === "vertical-lock") {
    return "Vertical-Lock (세로축-고정형)";
  }

  if (promptVersion === "aesthetic-freeform") {
    return "Aesthetic-Freeform (심미성-자유형)";
  }

  return unknownLabel;
};

export function SmileSimulationRecordDetailPage({
  comparison,
  download,
  isExperience = false,
  language,
  onBack,
  onDownloadResultImage,
  record,
}: SmileSimulationRecordDetailPageProps) {
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const copy = getSmileSimulationCopy(language);

  if (!record.patient) {
    return null;
  }

  const patient = record.patient;
  const [frameSize, setFrameSize] =
    useState<SmileSimulationController["camera"]["frameSize"]>(null);
  const { cameraFrameViewportRef } = useSmileSimulationViewport({
    onFrameSizeChange: setFrameSize,
  });
  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col gap-4 sm:gap-6">
      <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onBack}
              className="rounded-full border border-white/70 bg-white/90 px-4 py-2 text-sm font-semibold text-[#6b577d] shadow-[0_12px_30px_rgba(17,17,17,0.06)] transition hover:bg-white"
            >
              {isExperience ? "참가자 기록" : copy.recordDetail.back}
            </button>
          </div>
          {!isExperience ? (
            <div className="flex flex-wrap justify-end gap-2">
              <>
                <button
                  type="button"
                  onClick={onDownloadResultImage}
                  disabled={download.isDownloadingImage}
                  className="rounded-full border border-[#8F14E5]/20 bg-white/90 px-4 py-2 text-sm font-semibold text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {download.isDownloadingImage
                    ? copy.recordDetail.downloading
                    : copy.recordDetail.download}
                </button>
                <button
                  type="button"
                  aria-label="Toggle drawing tools"
                  aria-pressed={isDrawingEnabled}
                  onClick={() =>
                    setIsDrawingEnabled(
                      (currentIsDrawingEnabled) => !currentIsDrawingEnabled,
                    )
                  }
                  className={`flex h-10 w-10 items-center justify-center rounded-full border shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition ${
                    isDrawingEnabled
                      ? "border-[#8F14E5] bg-[#8F14E5] text-white"
                      : "border-[#8F14E5]/20 bg-white/90 text-[#8F14E5] hover:bg-white"
                  }`}
                >
                  <PenLine className="h-4 w-4" />
                </button>
              </>
            </div>
          ) : null}
        </div>

        <p className="text-center text-sm leading-6 text-[#5f3a78]">
          {copy.recordDetail.createdAt}{" "}
          {formatSmileSimulationDateTime(record.createdAt)}
        </p>

        <div className="grid gap-2 rounded-[1.5rem] border border-white/70 bg-white/90 p-4 text-sm text-[#5f3a78] shadow-[0_12px_30px_rgba(17,17,17,0.06)] sm:grid-cols-2">
          <div>
            <span className="font-semibold text-[#1f1230]">
              {copy.recordDetail.imageModel}
            </span>
            <p className="mt-1">
              {getSimulationProviderLabel(record, copy.recordDetail.unknown)}
            </p>
          </div>
          <div>
            <span className="font-semibold text-[#1f1230]">
              {copy.recordDetail.prompt}
            </span>
            <p className="mt-1">
              {getSimulationPromptLabel(
                record.promptVersion,
                copy.recordDetail.unknown,
              )}
            </p>
          </div>
        </div>

        <SmileSimulationPatientSummary
          caption="Simulation Record"
          isExperience={isExperience}
          language={language}
          patient={patient}
        />
      </>

      <div
        ref={cameraFrameViewportRef}
        className="flex min-h-0 w-full flex-1 items-center justify-center"
      >
        <div
          className="flex w-full max-w-full shrink-0 overflow-hidden rounded-[2rem] bg-[#13071f] shadow-[0_24px_60px_rgba(19,7,31,0.28)]"
          style={
            frameSize
              ? {
                  width: frameSize.width,
                  height: frameSize.height,
                }
              : { aspectRatio: "3 / 4" }
          }
        >
          <ComparisonSlider
            comparison={comparison}
            isDrawingEnabled={isDrawingEnabled}
            images={{
              after: record.resultImage,
              before: record.captureImage,
            }}
          />
        </div>
      </div>
    </section>
  );
}
